import {
  ChangeEvent,
  FormEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import {
  Form,
  FormContext,
  Modal,
  ModalContext,
  Toggle,
  toast,
  useServerResponseTranslation,
} from "dash-ui";

import { ExternalSalesModalKeys } from "pages/external-sales/constants/constants";
import { OrganizationUsersModalsEnum } from "pages/organizations/OrganizationDetailsPage/components/OrganizationDetailsTabs/components/OrganizationUsers/constants/constants";
import {
  GetSaleTeamUsersDocument,
  GetSaleTeamsDocument,
  SaleTeamUser,
  useAddSaleTeamUserMutation,
  useEditSaleTeamUserMutation,
  useGetSalesTeamRolesQuery,
} from "gql/generated.graphql";
import { useRestCall } from "common/hooks/useRestCall/useRestCall";
import { restApiCall } from "common/utils/restApiCall";

import { FormFields } from "./SaveSaleTeamUserModal.constants";
import useValidation from "./hooks/useValidation";

const SaveSaleTeamUserModal = () => {
  const { t } = useTranslation(["forms", "common", "pages"]);
  const { isOpenCurrent, toggleModal, payload, setPayload } =
    useContext(ModalContext);
  const isOpen =
    isOpenCurrent[ExternalSalesModalKeys.SaveSaleTeamUser] ||
    isOpenCurrent[ExternalSalesModalKeys.EditSaleTeamUser] ||
    false;
  const isEdit =
    !!isOpenCurrent[ExternalSalesModalKeys.EditSaleTeamUser] || false;
  const {
    formState,
    stateHandlers,
    setFormValues,
    formErrors,
    resetFormErrors,
    resetForm,
  } = useContext(FormContext);
  const valid = useValidation();

  const translateServerErrors = useServerResponseTranslation();
  const formRef = useRef<HTMLFormElement | null>(null);
  
  // Cache to store all properties that have been loaded (initial + search results)
  const propertiesCacheRef = useRef<Map<string, { _id: string; name: string }>>(new Map());
  
  // Track current search results to keep them visible after selection
  const [currentSearchResults, setCurrentSearchResults] = useState<Array<{ value: string; label: string }>>([]);

  // Initial load of properties (limited set)
  const { data: initialPropertiesData, loading: initialLoading } = useRestCall({
    endpoint: "internal-properties/dropdown",
    method: "POST",
    body: {
      pagination: { limit: 50, skip: 0 },
    },
    skip: !isOpen, // Skip if modal closed
  });

  // Load assigned properties when in edit mode
  const assignedPropertyIds = isEdit && payload ? (payload as SaleTeamUser).assignedPropertiesIds : undefined;
  const { data: assignedPropertiesData } = useRestCall({
    endpoint: "internal-properties/by-ids",
    method: "POST",
    body: {
      propertyIds: assignedPropertyIds || [],
    },
    skip: !isOpen || !isEdit || !assignedPropertyIds || assignedPropertyIds.length === 0,
  });


  const { data: rolesData, loading: rolesLoading } =
    useGetSalesTeamRolesQuery();

  // Update cache when initial data loads
  useEffect(() => {
    if (initialPropertiesData?.collection) {
      initialPropertiesData.collection.forEach((prop: { _id: string; name: string }) => {
        propertiesCacheRef.current.set(prop._id, prop);
      });
    }
  }, [initialPropertiesData]);

  // Update cache with assigned properties data
  useEffect(() => {
    if (assignedPropertiesData?.collection) {
      assignedPropertiesData.collection.forEach((prop: { _id: string; name: string }) => {
        propertiesCacheRef.current.set(prop._id, prop);
      });
    }
  }, [assignedPropertiesData]);

  // Custom load options that also updates the cache
  const loadPropertiesOptions = useDebouncedCallback(
    (
      inputValue: string,
      callback: (options: Array<{ label: string; value: string }>) => void
    ) => {
      if (inputValue.length < 3) {
        callback([]);
        setCurrentSearchResults([]); // Clear search results
        return;
      }

      restApiCall({
        endpoint: "internal-properties/dropdown",
        body: {
          search: inputValue,
          pagination: { limit: 50, skip: 0 },
        },
      })
        .then((data: any) => {
          // Update cache with search results
          data.collection.forEach((prop: { _id: string; name: string }) => {
            propertiesCacheRef.current.set(prop._id, prop);
          });
          
          const options = data.collection.map((item: { _id: string; name: string }) => ({
            value: item._id,
            label: item.name,
          }));
          
          // Store the current search results
          setCurrentSearchResults(options);
          callback(options);
        })
        .catch((error) => {
          console.error("Error searching properties:", error);
          callback([]);
          setCurrentSearchResults([]);
        });
    },
    500
  );

  const [addSaleTeamUser, { loading: addLoading }] = useAddSaleTeamUserMutation(
    {
      refetchQueries: [GetSaleTeamUsersDocument, GetSaleTeamsDocument],
    }
  );
  const [editSaleTeamUser, { loading: editLoading }] =
    useEditSaleTeamUserMutation({
      refetchQueries: [GetSaleTeamUsersDocument],
    });

  useEffect(() => {
    if (payload && isEdit) {
      const user = payload as SaleTeamUser;

      setFormValues({
        firstName: user.profile.firstName,
        lastName: user.profile.lastName,
        role: user.role._id,
        email: user.account.email,
        excludeFromReports: user.account.excludeFromReports,
        properties: user.assignedPropertiesIds,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, isEdit]);

  const defaultPropertiesOptions = useMemo(() => {
    // If we have active search results, return those combined with selected items
    if (currentSearchResults.length > 0) {
      const resultMap = new Map<string, { value: string; label: string }>();
      
      // Add search results
      currentSearchResults.forEach(option => {
        resultMap.set(option.value, option);
      });
      
      // Ensure selected properties are included
      if (formState.properties && Array.isArray(formState.properties)) {
        formState.properties.forEach((prop: any) => {
          const propId = typeof prop === 'string' ? prop : prop._id;
          if (propId && !resultMap.has(propId)) {
            const cachedProp = propertiesCacheRef.current.get(propId);
            if (cachedProp) {
              resultMap.set(propId, {
                value: cachedProp._id,
                label: cachedProp.name,
              });
            }
          }
        });
      }
      
      return Array.from(resultMap.values());
    }
    
    // Otherwise, show initial/default options
    const uniquePropertiesMap = new Map<string, { _id: string; name: string }>();
    
    // Add initial properties
    initialPropertiesData?.collection?.forEach((prop: { _id: string; name: string }) => {
      uniquePropertiesMap.set(prop._id, prop);
    });
    
    // Add assigned properties (for edit mode)
    assignedPropertiesData?.collection?.forEach((prop: { _id: string; name: string }) => {
      uniquePropertiesMap.set(prop._id, prop);
    });
    
    // Add currently selected properties from cache
    if (formState.properties && Array.isArray(formState.properties)) {
      formState.properties.forEach((prop: any) => {
        const propId = typeof prop === 'string' ? prop : prop._id;
        if (propId) {
          const cachedProp = propertiesCacheRef.current.get(propId);
          if (cachedProp) {
            uniquePropertiesMap.set(cachedProp._id, cachedProp);
          }
        }
      });
    }
    
    // Convert to options array
    return Array.from(uniquePropertiesMap.values()).map(property => ({
      value: property._id,
      label: property.name,
    }));
  }, [initialPropertiesData, assignedPropertiesData, formState.properties, currentSearchResults]);

  const rolesOptions = useMemo(() => {
    const roles = rolesData?.getSalesTeamRoles.collection || [];

    return roles.map((role) => ({
      value: role._id,
      label: t(`common:roles.${role.type}`),
    }));
  }, [rolesData, t]);

  const onRequestClose = () => {
    resetFormErrors();
    resetForm();
    setPayload(null);
    propertiesCacheRef.current.clear(); // Clear the cache
    setCurrentSearchResults([]); // Clear search results
    toggleModal(
      false,
      isEdit
        ? ExternalSalesModalKeys.EditSaleTeamUser
        : ExternalSalesModalKeys.SaveSaleTeamUser
    );
  };

  const handleTextValues =
    (field: FormFields) => (event: ChangeEvent<HTMLInputElement>) => {
      const handler = stateHandlers[field];
      handler(event.target.value);
      // valid(field, event.target.value);
    };

  const handleSelectValues =
    (field: FormFields) => (value: string, values: string[] | undefined) => {
      const handler = stateHandlers[field];
      handler(values || value);
      valid(field, value);
    };

  // Special handler for properties - just store IDs
  const handlePropertiesSelect = useCallback(
    (_value: string, values: string[] | undefined) => {
      if (!values || values.length === 0) {
        stateHandlers[FormFields.Properties]([]);
        return;
      }

      // Just store the IDs, not full objects
      stateHandlers[FormFields.Properties](values);
      // Note: Properties validation happens on form submit, not on change
    },
    [stateHandlers]
  );

  const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const {
      firstName,
      lastName,
      email,
      excludeFromReports,
      role,
      properties,
      ...profile
    } = formState;

    if (
      !valid(FormFields.FirstName, firstName) ||
      !valid(FormFields.LastName, lastName) ||
      !valid(FormFields.Email, email) ||
      !valid(FormFields.Role, role)
    )
      return;

    const mutation = isEdit ? editSaleTeamUser : addSaleTeamUser;

    mutation({
      variables: {
        input: {
          profileInput: {
            firstName: profile.firstName || firstName,
            lastName: profile.lastName || lastName,
          },
          accountInput: { email, excludeFromReports },
          saleTeamRoleId: role,
          saleTeamUserId: payload._id,
          assignedPropertiesIds: properties,
          saleTeamId: isEdit ? undefined : payload.saleTeamId,
        },
      },
      onCompleted: (userData: any) => {
        const dataKey = isEdit ? "editSaleTeamUser" : "addSaleTeamUser";
        const toastKey = isEdit ? "userUpdated" : "userAdded";
        onRequestClose();
        toast.success(t(`pages:externalSalesPage.toasts.${toastKey}`));
        toggleModal(
          true,
          OrganizationUsersModalsEnum.SEND_REGISTRATION_LINK_TO_USER
        );
        setPayload(userData[dataKey].accountId);
      },
      onError: (error) => {
        toast.error(translateServerErrors(error));
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title={
        (isEdit
          ? t("pages:externalSalesPage.editSaleTeamUserModalLabel")
          : t("pages:externalSalesPage.addSaleTeamUserModalLabel")) || ""
      }
      submitButton={{
        isLoading: addLoading || editLoading,
        title: isEdit ? t("common:labels.save") : t("common:labels.add"),
        onClick: () => {
          if (formRef) {
            formRef.current?.requestSubmit();
          }
        },
      }}
      cancelButton={{
        title: t("common:labels.cancel"),
        onClick: onRequestClose,
      }}
    >
      <Form ref={formRef} onSubmit={onFormSubmit}>
        <Form.Group w="100%" mt={3}>
          <Form.Label required>{t("forms:labels.email")}</Form.Label>
          <Form.Input
            placeholder={t("forms:placeholders.email") || ""}
            value={formState.email || ""}
            onChange={handleTextValues(FormFields.Email)}
            $errorType={formErrors[FormFields.Email]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.Email]?.type}>
            {formErrors[FormFields.Email]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label required>{t("forms:labels.firstName")}</Form.Label>
          <Form.Input
            placeholder={t("forms:placeholders.firstName") || ""}
            value={formState.firstName || ""}
            onChange={handleTextValues(FormFields.FirstName)}
            $errorType={formErrors[FormFields.FirstName]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.FirstName]?.type}>
            {formErrors[FormFields.FirstName]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label required>{t("forms:labels.lastName")}</Form.Label>
          <Form.Input
            placeholder={t("forms:placeholders.lastName") || ""}
            onChange={handleTextValues(FormFields.LastName)}
            value={formState.lastName || ""}
            $errorType={formErrors[FormFields.LastName]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.LastName]?.type}>
            {formErrors[FormFields.LastName]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label required>{t("forms:labels.role")}</Form.Label>
          <Form.Select
            w="100%"
            isLoading={rolesLoading}
            options={rolesOptions}
            value={formState.role || ""}
            placeholder={t("forms:placeholders.role")}
            onChanged={handleSelectValues(FormFields.Role)}
            $errorType={formErrors[FormFields.Role]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.Role]?.type}>
            {formErrors[FormFields.Role]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label>{t("forms:labels.properties")}</Form.Label>
          <Form.Select
            isAsync
            isMulti
            isSelectAll
            defaultOptions={defaultPropertiesOptions}
            isLoading={initialLoading}
            value={formState.properties?.map((prop: any) => 
              typeof prop === 'string' ? prop : prop._id
            ) || []}
            loadOptions={loadPropertiesOptions}
            onChanged={handlePropertiesSelect}
            w="100%"
            placeholder={t("forms:placeholders.properties")}
            noOptionsMessage={({ inputValue }) =>
              inputValue && inputValue.length < 3
                ? "Type at least 3 characters to search"
                : "No properties found"
            }
          />
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label>{t("forms:labels.excludeFromReports")}</Form.Label>
          <Toggle
            // @ts-ignore
            checked={formState.excludeFromReports}
            flexProps={{ mx: 2, my: 2 }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { checked } = e.target;
              stateHandlers[FormFields.ExcludeFromReports](checked);
            }}
          />
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default SaveSaleTeamUserModal;
